import { useMemo, useState } from "react";
import type { ContentItem } from "@/types/content";

export type SortKey = "new" | "hot";

export function useContentExplorer(baseItems: ContentItem[], perPage = 8) {
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("new");
  const [page, setPage] = useState(1);

  const toggleRegion = (v: string) => {
    setSelectedRegions((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    setPage(1);
  };
  const toggleYear = (v: string) => {
    setSelectedYears((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    setPage(1);
  };
  const toggleGenre = (v: string) => {
    setSelectedGenres((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    setPage(1);
  };
  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedYears([]);
    setSelectedGenres([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    return baseItems.filter((item) => {
      const regionOk = selectedRegions.length === 0 || selectedRegions.includes(item.region);
      const yearOk = selectedYears.length === 0 || selectedYears.includes(String(item.year));
      const genreOk = selectedGenres.length === 0 || item.genres.some((g) => selectedGenres.includes(g));
      return regionOk && yearOk && genreOk;
    });
  }, [baseItems, selectedRegions, selectedYears, selectedGenres]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "hot") {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    }
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * perPage, safePage * perPage),
    [sorted, safePage, perPage],
  );

  return {
    selected: { region: selectedRegions, year: selectedYears, genre: selectedGenres },
    toggleRegion,
    toggleYear,
    toggleGenre,
    clearFilters,
    sort,
    setSort: (s: SortKey) => {
      setSort(s);
      setPage(1);
    },
    page: safePage,
    setPage,
    total: sorted.length,
    totalPages,
    paged,
    hasActiveFilters: selectedRegions.length + selectedYears.length + selectedGenres.length > 0,
  };
}